import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Compbaser } from "../../../../components/compbaser";
import { Id, IPlayerProps, ISceneBlock, ISendEvents } from "../../../../interfaces";
import { actionAddSceneItem, actionUpdateSceneItem } from "../../../../store/actions/user.actions";
import { AppService, SceneAddService, StoreSelectorsService } from "../../../../services";
import { Slide, TouchLib } from "../touchLib";
import * as _ from "lodash";

@Component({
    selector: "app-touch-action-dialog",
    templateUrl: "./touch-action-dialog.component.html",
    styleUrls: ["./touch-action-dialog.component.scss"],
    standalone: false
})
export class DialogTouchActionComponent extends Compbaser implements OnInit {
    sliderScene: IPlayerProps;
    sliderSceneId;
    selectedSceneId: string;
    selectedSceneItemId: string;
    commandList: { name: string; value: string }[] = [];
    selectedCommand = "firstPage";
    customButtonText = ""; // Custom text for Fixed button
    width: string;
    height: string;
    previewHeight = 70;
    slideList: Slide[];
    linkSlideSceneId: Id;
    collectionId;
    offset;

    // New properties for enhanced dialog
    viewMode: 'grid' | 'list' = 'grid';
    searchTerm = '';
    filteredSlideList: Slide[] = [];
    newTouchButton = true;

    constructor(
        private storeSelector: StoreSelectorsService,
        private sceneAddService: SceneAddService,
        private appService: AppService,
        public dialogRef: MatDialogRef<any>, 
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        super();

        this.commandList = [
            { name: "First", value: "firstPage" },
            { name: "Prev", value: "prevPage" },
            { name: "Next", value: "nextPage" },
            { name: "Last", value: "lastPage" },
            { name: "Fixed", value: "selectPage" },
        ];

        if (data) {
            this.sliderScene = data.sliderScene;
            this.newTouchButton = !!data.newTouchButton;
            this.offset = data.offset;
        }
    }

    ngOnInit() {
        this.configureProps();
        this.sceneAddService.setBlockType("BLOCK_COMPONENTS");

        this.cod(
            this.storeSelector.listenSelectedSceneIdAndSceneItemId()
                .subscribe((value) => {
                    this.selectedSceneId = value.sceneId;
                    this.selectedSceneItemId = value.sceneItemId;

                    // Check for existing event binding when scene item is selected
                    if (this.selectedSceneItemId && !this.newTouchButton) {
                        this.checkForExistingEventBinding();
                    }
                })
        );
    }

    private configureProps() {
        this.sliderSceneId = null;
        if (this.sliderScene) {
            this.sliderSceneId = this.sliderScene?.Player?.$?.id;
            this.collectionId = TouchLib.getPlaylistId(this.sliderScene);

            const basicInfo = TouchLib.getSliderBasicInfo(this.sliderScene);
            this.width = basicInfo.width;
            this.height = basicInfo.height;
            this.slideList = TouchLib.extractSlidesFromSlider(this.sliderScene);
            this.filteredSlideList = [...this.slideList];

            // Check for existing event binding after slide list is loaded
            if (this.selectedSceneItemId && !this.newTouchButton) {
                setTimeout(() => {
                    this.checkForExistingEventBinding();
                }, 100);
            }
        } else {
            this.dialogRef.close();
        }
    }

    private checkForExistingEventBinding() {
        if (!this.selectedSceneId || !this.selectedSceneItemId) return;

        this.cod(
            this.storeSelector.listenPlayerDataOfSelectedSceneItemId()
                .subscribe((sceneItemData) => {
                    const clickEventName = sceneItemData?.sceneItem?.$.clickEventName;
                    if (!clickEventName) return;

                    // Find matching event in collection's EventCommands
                    const componentName = TouchLib.getComponentName(this.sliderScene);
                    const players = this.sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
                    const componentPlayer = players?.find(p => p.Data?.[0]?.[componentName]);
                    const eventCommands = componentPlayer?.Data?.[0]?.EventCommands;
                    const matchingEvent = eventCommands?.[0]?.EventCommand?.find(event =>
                        event.$.from === clickEventName
                    );

                    if (matchingEvent) {
                        const command = matchingEvent.$.command;

                        // Handle "selectPage" (Fixed) command
                        if (command === 'selectPage') {
                            const pageName = matchingEvent.Params?.[0]?.Page?.[0]?.$.name;
                            const targetSlide = this.slideList.find(slide => slide.privateName === pageName);

                            if (targetSlide) {
                                this.selectedCommand = 'selectPage';
                                this.linkSlideSceneId = targetSlide.sceneId;
                                this.customButtonText = sceneItemData.sceneItem.$?.label || '';
                            }
                        }
                        // Handle navigation commands (first, next, last, previous)
                        else if (['firstPage', 'nextPage', 'lastPage', 'prevPage'].includes(command)) {
                            this.selectedCommand = command;
                            this.linkSlideSceneId = null; // Clear any slide selection for navigation commands
                            this.customButtonText = sceneItemData.sceneItem.$?.label || '';
                        }
                    }
                })
        );
    }

    onAddTouchAction() {
        let newButtonBlock: ISceneBlock;
        let buttonName;
        if (this.newTouchButton) {
            newButtonBlock = _.cloneDeep(this.storeSelector.blockLabels.BUTTON.template);
        }
        
        const template = this.getSendEventTemplate();
        const eventName = TouchLib.touchEventName(`${this.selectedCommand}`);
        
        if (this.selectedCommand === "selectPage") {
            const page = this.slideList.find((s) => s.sceneId === this.linkSlideSceneId);
            if (!page) return;
            template.Params[0].Page[0].$.name = page.privateName;
        } else {
            template.Params[0].Page[0].$.name = "Page";
        }
        
        template.$.from = eventName;
        template.$.command = this.selectedCommand;

        // Determine button name: custom text if provided, otherwise default
        if (this.customButtonText.trim() && this.newTouchButton) {
            buttonName = this.customButtonText.trim();
        } else {
            // Use default names for each command
            const selectedCommandObj = this.commandList.find(cmd => cmd.value === this.selectedCommand);
            if (selectedCommandObj) {
                buttonName = selectedCommandObj.name;
            }
        }

        if (newButtonBlock) {
            newButtonBlock.$.clickEventName = eventName;
            newButtonBlock.$.clickEventParam = "";
            if (buttonName) {
                newButtonBlock.Data[0].Button[0].$.label = buttonName;
            }

            // Make the button rectangular (wider than tall)
            const layout = newButtonBlock.Data["0"].Layout["0"]?.$;
            if (layout) {
                // Set rectangular dimensions - 2:1 width to height ratio
                layout.width = "160";
                layout.height = "80";
            }
            
            // Add droped touch button
            this.sceneAddService.addScenePlayers([newButtonBlock], this.offset?.offsetX || 0, this.offset?.offsetY || 0, null, true, false);
        } else {
            this.storeSelector.dispatch(actionUpdateSceneItem({
                sceneId: this.selectedSceneId,
                sceneItemId: this.selectedSceneItemId,
                values: [
                    {
                        key: "clickEventName",
                        value: eventName,
                        storePath: "$",
                    },
                    {
                        key: "clickEventParam",
                        value: "",
                        storePath: "$",
                    }
                ],
                disableCRUD: true,
            }));
        }

        setTimeout(() => {
            this.updateCollectionEvent(template);
            this.closeModal();
        }, 200);
        
        setTimeout(() => {
            this.appService.updateMultiScene([this.sliderSceneId, this.selectedSceneId]);
        }, 1000);
    }

    private updateCollectionEvent(eventCommand) {
        // Update the EventCommands of Collection
        const componentName = TouchLib.getComponentName(this.sliderScene);
        const players = this.sliderScene.Player.Data[0].Scene[0].Players[0].Player;
        const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
        if (!componentPlayer) return;
        const eventCommands = componentPlayer.Data[0].EventCommands;

        if (eventCommands && eventCommands[0]?.EventCommand) {
            const isExisting = eventCommands[0].EventCommand.find((_command) => _command.$.from === eventCommand.$.from);
            if (!isExisting) {
                this.storeSelector.dispatch(
                    actionAddSceneItem({
                        sceneId: this.sliderSceneId,
                        sceneItemId: this.collectionId,
                        values: [
                            {
                                key: "EventCommand",
                                value: eventCommand,
                                storePath: `Data[0].EventCommands[0]`,
                            },
                        ],
                        disableCRUD: true,
                    }),
                );
            }
        } else {
            this.storeSelector.dispatch(
                actionUpdateSceneItem({
                    sceneId: this.sliderSceneId,
                    sceneItemId: this.collectionId,
                    values: [
                        {
                            key: "EventCommands",
                            value: [
                                {
                                    EventCommand: [eventCommand],
                                },
                            ],
                            storePath: `Data[0]`,
                        },
                    ],
                    disableCRUD: true,
                }),
            );
        }
    }

    closeModal() {
        this.reset();
        this.dialogRef.close();
    }

    reset() {
        this.selectedCommand = "firstPage";
        this.linkSlideSceneId = null;
        this.customButtonText = "";
    }

    selectSlideToLink(slideSceneId) {
        this.linkSlideSceneId = slideSceneId;
        // Auto-select "Fixed" when user picks a specific slide
        this.selectedCommand = 'selectPage';
    }

    onCommandChange(event: any) {
        const command = event.value;
        // Clear slide selection when user selects navigation commands (First/Prev/Next/Last)
        if (command !== 'selectPage') {
            this.linkSlideSceneId = null;
        }
    }

    // New methods for enhanced dialog
    setViewMode(mode: 'grid' | 'list') {
        this.viewMode = mode;
    }

    onSearchChange() {
        this.filterSlides();
    }

    clearSearch() {
        this.searchTerm = '';
        this.filterSlides();
    }

    private filterSlides() {
        if (!this.searchTerm.trim()) {
            this.filteredSlideList = [...this.slideList];
            return;
        }

        const term = this.searchTerm.toLowerCase();
        this.filteredSlideList = this.slideList.filter((slide, index) => {
            const slideName = (slide.title || `Slide ${index + 1}`).toLowerCase();
            const slideIndex = (index + 1).toString();
            return slideName.includes(term) || slideIndex.includes(term) || slide.sceneId.includes(term);
        });
    }

    getPlaceholderText(): string {
        const selectedCommandObj = this.commandList.find(cmd => cmd.value === this.selectedCommand);
        const defaultName = selectedCommandObj ? selectedCommandObj.name : 'Button';

        if (this.selectedCommand === 'selectPage') {
            return `Enter custom text (default: "${defaultName}")`;
        } else {
            return `Enter custom text (default: "${defaultName}")`;
        }
    }

    getHintText(): string {
        const selectedCommandObj = this.commandList.find(cmd => cmd.value === this.selectedCommand);
        const defaultName = selectedCommandObj ? selectedCommandObj.name : 'Button';

        return `Leave empty to use default text "${defaultName}" (max 50 characters)`;
    }

    private getSendEventTemplate(): ISendEvents {
        return {
            $: {
                from: "",
                command: "",
            },
            Params: [
                {
                    Page: [
                        {
                            $: {
                                name: "",
                                page: "",
                                type: "",
                            },
                            Player: [
                                {
                                    $: {},
                                },
                            ],
                        },
                    ],
                },
            ],
        };
    }
}
